import React, { useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import SimplePanel from '@/shared/components/SimplePanel';
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import EditableTextbox from '@/shared/components/editable/EditableTextbox';
import Button from '@/shared/components/Button';
import YesNoModal from '@/shared/components/YesNoModal';
import { CanCreate } from '@/shared/components/security';
import AuthService, { functionalities, privileges } from '@/shared/services/AuthService';
import ToastrService from '@/shared/services/ToastrService';
import { useGetEmployeeProfiles } from '@/shared/hooks/employeeProfile/useGetEmployeeProfiles';
import {
  useCreateEmployeeProfile,
  useUpdateEmployeeProfile,
  useDeleteEmployeeProfile,
  useSetDefaultEmployeeProfile,
} from '@/shared/hooks/employeeProfile/useEmployeeProfileMutations';
import AddEmployeeProfileModal from './AddEmployeeProfileModal';
import messages from './employeeProfileMessages';

const EmployeeProfileOverviewContainer = () => {
  const intl = useIntl();
  const { data: profiles, isLoading } = useGetEmployeeProfiles();
  const { createProfile } = useCreateEmployeeProfile();
  const { updateProfile } = useUpdateEmployeeProfile();
  const { deleteProfile } = useDeleteEmployeeProfile();
  const { setDefault } = useSetDefaultEmployeeProfile();

  const [showAddModal, setShowAddModal] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);

  const canUpdate = AuthService.hasPermission({
    functionality: functionalities.CompanyOwn,
    privilege: privileges.Update,
  });
  const canDelete = AuthService.hasPermission({
    functionality: functionalities.CompanyOwn,
    privilege: privileges.Delete,
  });

  const sortedProfiles = useMemo(
    () => [...(profiles || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [profiles],
  );

  const handleNameChange = async (profile, newName) => {
    if (newName === profile.name || !newName) return;
    await updateProfile({ ...profile, name: newName });
    ToastrService.success(intl.formatMessage(messages.ChangesSaved));
  };

  const handleAdd = async name => {
    await createProfile({ name });
    ToastrService.success(intl.formatMessage(messages.ChangesSaved));
    setShowAddModal(false);
  };

  const handleDelete = async () => {
    if (!profileToDelete?.id || profileToDelete.isDefault) return;
    await deleteProfile(profileToDelete.id);
    ToastrService.success(intl.formatMessage(messages.ChangesSaved));
    setProfileToDelete(null);
  };

  const handleSetDefault = async profile => {
    if (!profile.id || profile.isDefault) return;
    await setDefault(profile.id);
    ToastrService.success(intl.formatMessage(messages.ChangesSaved));
  };

  return (
    <div id="EmployeeOverviewPage">
      <SimplePanel
        title={<FormattedMessage {...messages.PriceProfiles} />}
        article="5434829034770"
        buttons={
          <CanCreate on={functionalities.CompanyOwn}>
            <Button
              btnClassName="btn btn-inverse btn-sticky btn-sticky-left"
              onClick={() => setShowAddModal(true)}
              title={intl.formatMessage(messages.AddEmployeeProfile)}
            >
              <i className="fa fa-plus" />
              <span>
                <FormattedMessage {...messages.Add} />
              </span>
            </Button>
          </CanCreate>
        }
      >
        <LoadingContainer isLoading={isLoading}>
          <table className="table table-grid table-hover">
            <thead>
              <tr>
                <th className="sticky-header">
                  <FormattedMessage {...messages.Name} />
                </th>
                <th className="sticky-header">
                  <FormattedMessage {...messages.AssignedEmployees} />
                </th>
                <th className="sticky-header text-center icon">
                  <FormattedMessage {...messages.Default} />
                </th>
                {canDelete && (
                  <th className="text-center icon sticky-header">
                    <FormattedMessage {...messages.Delete} />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {sortedProfiles.map(profile => (
                <tr key={profile.id}>
                  <td>
                    <EditableTextbox
                      value={profile.name}
                      onChange={newName => handleNameChange(profile, newName)}
                      readOnly={!canUpdate}
                      required
                    />
                  </td>
                  <td>
                    <label>{profile.assignedEmployees}</label>
                  </td>
                  <td className="text-center">
                    {profile.isDefault ? (
                      <i
                        className="fa fa-star text-ochre"
                        title={intl.formatMessage(messages.DefaultProfile)}
                      />
                    ) : canUpdate ? (
                      <a
                        onClick={() => handleSetDefault(profile)}
                        title={intl.formatMessage(messages.MakeDefaultProfile)}
                      >
                        <i className="fa fa-star-o" />
                      </a>
                    ) : (
                      <i className="fa fa-star-o" />
                    )}
                  </td>
                  {canDelete && (
                    <td className="text-center">
                      {!profile.isDefault && (
                        <a onClick={() => setProfileToDelete(profile)} className="fa fa-trash-o" />
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </LoadingContainer>
      </SimplePanel>

      {showAddModal && (
        <AddEmployeeProfileModal show onClose={() => setShowAddModal(false)} onSave={handleAdd} />
      )}

      {profileToDelete && (
        <YesNoModal
          show
          title={intl.formatMessage(messages.ConfirmDeleteTitle)}
          message={intl.formatMessage(messages.ConfirmDeleteEmployeeProfile)}
          onNo={() => setProfileToDelete(null)}
          onSave={handleDelete}
        />
      )}
    </div>
  );
};

export default EmployeeProfileOverviewContainer;
